import React, { useCallback, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import { mandateRegisterV2, getUccV2 } from "../../../api/bseApi";
import {
  buildMandateRegisterPayload,
  bseIstToday,
  defaultValidTill,
  extractBseApiError,
  formatMandateRegisterError,
  getMandateRegistrationLink,
  maxValidTillFromStart,
  parseUccForMandateBank,
  resolveMandateMode,
} from "../../../utils/bseMandate";

const TEAL = "#06b6a4";
/** IFSC: 11 chars — 4 bank letters + 0 + 6 alphanumeric branch code (e.g. BARB0MANPOA) */
const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const STEPS = [
  { id: 0, title: "Investor", subtitle: "Client UCC and member code" },
  { id: 1, title: "Bank account", subtitle: "Account to debit for the mandate" },
  { id: 2, title: "Mandate terms", subtitle: "Type, amount, and validity dates" },
  { id: 3, title: "Member details", subtitle: "EUIN / sub-broker (optional)" },
  { id: 4, title: "Entry info", subtitle: "Required only for ENTRY (excluded member)" },
];

function isoToday() {
  return bseIstToday();
}

function initialValidTill() {
  return defaultValidTill(bseIstToday());
}

const INITIAL_FORM = {
  member: (process.env.REACT_APP_BSE_MEMBER_ID || "63164").trim(),
  ucc: "",
  ifsc: "",
  account_no: "",
  bank_type: "SB",
  bank_name: "",
  bank_branch: "",
  vpa: "",
  amount: "15000",
  start_date: isoToday(),
  valid_till: initialValidTill(),
  reg_date: isoToday(),
  mandate_type: (process.env.REACT_APP_BSE_DEFAULT_MANDATE_TYPE || "U").trim().toUpperCase(),
  mode: "DD",
  frequency: "AS AND WHEN PRESENTED",
  // BSE portal / working Postman: REGISTRATION for normal UPI/eNACH/NACH
  request_type: "REGISTRATION",
  redirect_url: "",
  euin: (process.env.REACT_APP_BSE_EUIN || "E603747").trim(),
  euin_flag: true,
  sub_br_code: (process.env.REACT_APP_BSE_SUB_BR_CODE || "").trim(),
  sub_br_arn: (process.env.REACT_APP_BSE_ARN || process.env.REACT_APP_BSE_SUB_BR_ARN || "ARN-315999").trim(),
  partner_id: (process.env.REACT_APP_BSE_PARTNER_ID || "").trim(),
  member_mandate_id: "",
  mandate_status_date: new Date().toISOString(),
  umrn_number: "",
  utility_code: "",
  sponsor_code: "",
};

export default function CreateMandate() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [uccBankLoading, setUccBankLoading] = useState(false);
  const [uccBankNote, setUccBankNote] = useState("");
  const [uccBankOnFile, setUccBankOnFile] = useState(null);

  const loadUccBank = useCallback(async (ucc, member) => {
    const code = String(ucc || "").trim();
    if (!code) return;
    setUccBankLoading(true);
    setUccBankNote("");
    try {
      const res = await getUccV2({
        data: {
          member: { member_id: String(member || form.member).trim() },
          investor: { client_code: code },
        },
      });
      const body = res?.data || {};
      if (body.status !== "success" && body.status !== 1) {
        throw new Error(extractBseApiError({ response: { data: body } }));
      }
      const bank = parseUccForMandateBank(body);
      if (!bank.ifsc || !bank.account_no) {
        setUccBankNote("No bank account found on this UCC in BSE.");
        return;
      }
      setUccBankOnFile({ ifsc: bank.ifsc, account_no: bank.account_no, bank_name: bank.bank_name });
      setForm((prev) => ({
        ...prev,
        ifsc: bank.ifsc,
        account_no: bank.account_no,
        bank_type: bank.bank_type,
        bank_name: bank.bank_name,
      }));
      setUccBankNote(
        `Loaded from BSE UCC: ${bank.ifsc} · ${bank.account_no} · ${bank.bank_name || "holder"}`
      );
    } catch (err) {
      setUccBankNote(extractBseApiError(err));
    } finally {
      setUccBankLoading(false);
    }
  }, [form.member]);

  const visibleSteps = useMemo(
    () =>
      String(form.request_type).toUpperCase() === "ENTRY"
        ? STEPS
        : STEPS.filter((s) => s.id !== 4),
    [form.request_type]
  );

  const update = useCallback((patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (patch.mandate_type) {
        next.mode = resolveMandateMode(patch.mandate_type, next.mode);
      }
      if (patch.start_date) {
        const maxTill = maxValidTillFromStart(patch.start_date);
        if (maxTill) next.valid_till = maxTill;
        next.reg_date = patch.start_date;
      }
      if (patch.mandate_type === "U") next.mode = "DD";
      if (patch.mandate_type === "N" || patch.mandate_type === "X") next.mode = "ACH";
      return next;
    });
  }, []);

  const validateStep = (s) => {
    if (s === 0) {
      if (!form.ucc.trim()) return "Client UCC is required";
      if (!form.member.trim()) return "Member code is required";
    }
    if (s === 1) {
      if (!form.ifsc.trim()) return "IFSC is required";
      if (!ifscRegex.test(form.ifsc.trim().toUpperCase())) return "Invalid IFSC format";
      if (!form.account_no.trim()) return "Account number is required";
      if (!form.bank_name.trim()) return "Account holder name is required";
    }
    if (s === 2) {
      const amt = Number(form.amount);
      if (!Number.isFinite(amt) || amt <= 0) return "Mandate amount must be greater than 0";
      if (!form.start_date) return "Start date is required";
      if (!form.valid_till) return "Valid till date is required";
      const today = bseIstToday();
      if (form.start_date < today) return `Start date must be on or after today (IST: ${today})`;
      const maxTill = maxValidTillFromStart(form.start_date);
      if (form.valid_till > maxTill) {
        return `Valid till cannot be more than 30 years after start (max ${maxTill})`;
      }
      if (form.start_date >= form.valid_till) return "Valid till must be after start date";
      if (form.mandate_type === "U") {
        const vpa = String(form.vpa || "").trim();
        if (!vpa || !vpa.includes("@")) return "UPI AutoPay requires a valid VPA (e.g. 9876543210@ybl)";
      }
    }
    if (s === 4 && String(form.request_type).toUpperCase() === "ENTRY") {
      if (!form.member_mandate_id.trim()) {
        return "Member mandate ID is required for ENTRY (excluded member)";
      }
    }
    return null;
  };

  const goNext = async () => {
    const err = validateStep(step);
    if (err) {
      Swal.fire({ icon: "warning", title: "Check form", text: err });
      return;
    }
    if (step === 0) {
      await loadUccBank(form.ucc, form.member);
    }
    const idx = visibleSteps.findIndex((s) => s.id === step);
    if (idx < visibleSteps.length - 1) setStep(visibleSteps[idx + 1].id);
  };

  const goBack = () => {
    const idx = visibleSteps.findIndex((s) => s.id === step);
    if (idx > 0) setStep(visibleSteps[idx - 1].id);
  };

  const handleSubmit = async () => {
    for (const s of visibleSteps) {
      const err = validateStep(s.id);
      if (err) {
        Swal.fire({ icon: "warning", title: "Check form", text: err });
        setStep(s.id);
        return;
      }
    }

    if (uccBankOnFile) {
      const ifsc = form.ifsc.trim().toUpperCase();
      const acct = form.account_no.trim();
      if (ifsc !== uccBankOnFile.ifsc.toUpperCase() || acct !== uccBankOnFile.account_no) {
        Swal.fire({
          icon: "warning",
          title: "Bank mismatch",
          text: "Mandate bank must match the UCC account on BSE. Click “Load bank from UCC” or use the loaded IFSC and account number.",
        });
        setStep(1);
        return;
      }
    }

    const redirectUrl =
      form.redirect_url.trim() || `${window.location.origin}/mandate/return`;
    const payload = buildMandateRegisterPayload({ ...form, redirect_url: redirectUrl });

    setSubmitting(true);
    try {
      const res = await mandateRegisterV2(payload);
      const body = res?.data || {};
      if (body.status !== "success") {
        throw new Error(extractBseApiError({ response: { data: body } }));
      }

      const link = getMandateRegistrationLink(body);
      const exchId = body.data?.exch_mandate_id || body.data?.id || "-";

      if (link) {
        await Swal.fire({
          icon: "success",
          title: "Mandate initiated",
          html: `
            <p>BSE exchange mandate ID: <strong>${exchId}</strong></p>
            <p class="small text-muted mb-0">Complete authentication in the bank / UPI flow.</p>
          `,
          confirmButtonText: "Continue to bank",
        });
        window.location.href = link;
        return;
      }

      await Swal.fire({
        icon: "success",
        title: "Mandate registered",
        html: `
          <p>BSE exchange mandate ID: <strong>${exchId}</strong></p>
          <p class="small text-muted mb-0">UCC: ${form.ucc.trim()}</p>
        `,
      });
      setForm({ ...INITIAL_FORM, redirect_url: form.redirect_url });
      setStep(0);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Mandate registration failed",
        html: formatMandateRegisterError(error),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = visibleSteps.findIndex((s) => s.id === step);
  const progressPct = ((stepIndex + 1) / visibleSteps.length) * 100;
  const isLastStep = stepIndex === visibleSteps.length - 1;

  const inputStyle = {
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    padding: "8px 12px",
    fontSize: "14px",
    width: "100%",
  };

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
            <div className="card-body p-4">
              <nav aria-label="breadcrumb" className="mb-2">
                <ol className="breadcrumb mb-0 small">
                  <li className="breadcrumb-item">
                    <NavLink to="/admin">Admin</NavLink>
                  </li>
                  <li className="breadcrumb-item">
                    <NavLink to="/admin/invest-bse/mandate-list">Invest Online BSE</NavLink>
                  </li>
                  <li className="breadcrumb-item active">Register Mandate</li>
                </ol>
              </nav>
              <h3 className="mb-1" style={{ color: "#0f172a" }}>
                Register BSE Mandate
              </h3>
              <p className="text-muted mb-0">
                eNACH, NACH, or UPI AutoPay via BSE <code>/mandate_register</code>. UAT eNACH: use amount &lt; ₹90.
              </p>
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-3" style={{ borderRadius: "12px" }}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">
                  Step {stepIndex + 1} of {visibleSteps.length}:{" "}
                  <strong>{visibleSteps[stepIndex]?.title}</strong>
                </span>
              </div>
              <div style={{ height: 8, background: "#e2e8f0", borderRadius: 99, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${progressPct}%`,
                    height: "100%",
                    background: `linear-gradient(90deg, ${TEAL}, #0891b2)`,
                    transition: "width 0.25s ease",
                  }}
                />
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0" style={{ borderRadius: "12px", maxWidth: 760, margin: "0 auto" }}>
            <div className="card-body p-4">
              <p className="text-muted small mb-4">{visibleSteps[stepIndex]?.subtitle}</p>

              {step === 0 && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small">BSE member code</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.member}
                      onChange={(e) => update({ member: e.target.value.replace(/\D/g, "") })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Client UCC</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.ucc}
                      onChange={(e) => update({ ucc: e.target.value })}
                      placeholder="Active BSE client code"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Request type</label>
                    <select
                      className="form-select"
                      style={inputStyle}
                      value={form.request_type}
                      onChange={(e) => update({ request_type: e.target.value })}
                    >
                      <option value="REGISTRATION">REGISTRATION (normal mandate)</option>
                      <option value="ENTRY">ENTRY (excluded member only)</option>
                    </select>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="alert alert-info py-2 small mb-0">
                      Mandate bank must match the account registered on the UCC in BSE. Use{" "}
                      <strong>Load from UCC</strong> to auto-fill.
                    </div>
                    {uccBankNote && (
                      <p className="small text-muted mt-2 mb-0">{uccBankNote}</p>
                    )}
                  </div>
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={uccBankLoading}
                      onClick={() => loadUccBank(form.ucc, form.member)}
                    >
                      {uccBankLoading ? "Loading…" : "Load bank from UCC"}
                    </button>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">IFSC</label>
                    <input
                      className="form-control text-uppercase"
                      style={inputStyle}
                      value={form.ifsc}
                      onChange={(e) => update({ ifsc: e.target.value.toUpperCase() })}
                      maxLength={11}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Account number</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.account_no}
                      onChange={(e) => update({ account_no: e.target.value.replace(/\D/g, "") })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Account type</label>
                    <select
                      className="form-select"
                      style={inputStyle}
                      value={form.bank_type}
                      onChange={(e) => update({ bank_type: e.target.value })}
                    >
                      <option value="SB">Savings (SB)</option>
                      <option value="CA">Current (CA)</option>
                      <option value="CB">Cash credit (CB)</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Account holder name</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.bank_name}
                      onChange={(e) => update({ bank_name: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">Branch (optional)</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.bank_branch}
                      onChange={(e) => update({ bank_branch: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small">Mandate type</label>
                    <select
                      className="form-select"
                      style={inputStyle}
                      value={form.mandate_type}
                      onChange={(e) => update({ mandate_type: e.target.value })}
                    >
                      <option value="U">UPI AutoPay (U)</option>
                      <option value="N">eNACH (N)</option>
                      <option value="X">NACH (X)</option>
                    </select>
                    <p className="small text-muted mt-1 mb-0">
                      Type must be enabled by BSE for member {form.member || "63164"} (agency_code).
                      If UPI fails with “No agency found”, try eNACH/NACH or ask BSE to enable the agency.
                    </p>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Mode</label>
                    <select
                      className="form-select"
                      style={inputStyle}
                      value={resolveMandateMode(form.mandate_type, form.mode)}
                      onChange={(e) => update({ mode: e.target.value })}
                      disabled={form.mandate_type === "U" || form.mandate_type === "N" || form.mandate_type === "X"}
                    >
                      <option value="ACH">ACH (eNACH / NACH)</option>
                      <option value="DD">DD (UPI AutoPay)</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Frequency</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.frequency}
                      onChange={(e) => update({ frequency: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Maximum amount (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      style={inputStyle}
                      value={form.amount}
                      onChange={(e) => update({ amount: e.target.value })}
                      min={1}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Registration date (IST, set on submit)</label>
                    <input
                      type="date"
                      className="form-control"
                      style={inputStyle}
                      value={bseIstToday()}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Start date</label>
                    <input
                      type="date"
                      className="form-control"
                      style={inputStyle}
                      value={form.start_date}
                      min={bseIstToday()}
                      onChange={(e) => update({ start_date: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Valid till (max 30 years from start)</label>
                    <input
                      type="date"
                      className="form-control"
                      style={inputStyle}
                      value={form.valid_till}
                      max={maxValidTillFromStart(form.start_date) || undefined}
                      onChange={(e) => update({ valid_till: e.target.value })}
                    />
                  </div>
                  <div className="col-12">
                    <p className="small text-muted mb-2">
                      Dates: start ≥ today (IST); valid till ≤ start + 30 years − 1 day (BSE max duration).
                      (BSE Postman pattern). Amount tip on UAT eNACH is often small.
                    </p>
                    <p className="small text-danger mb-2">
                      If BSE returns <em>agency_code / No agency found</em> for type N or U, that is a BSE
                      account setup issue for member <strong>63164</strong>. Ask BSE to enable mandate
                      agencies for eNACH (N) and UPI (U) on StAR MF v2 demo — ARN/EUIN cannot unlock this.
                    </p>
                  </div>
                  {form.mandate_type === "U" && (
                    <div className="col-12">
                      <label className="form-label small">UPI VPA (required for UPI)</label>
                      <input
                        className="form-control"
                        style={inputStyle}
                        value={form.vpa}
                        onChange={(e) => update({ vpa: e.target.value.trim() })}
                        placeholder="9876543210@ybl"
                      />
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label small">Redirect URL (eNACH)</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.redirect_url}
                      onChange={(e) => update({ redirect_url: e.target.value })}
                      placeholder={`${window.location.origin}/mandate/return`}
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small">EUIN</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.euin}
                      onChange={(e) => update({ euin: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 d-flex align-items-end">
                    <label className="d-flex align-items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={form.euin_flag}
                        onChange={(e) => update({ euin_flag: e.target.checked })}
                      />
                      EUIN declaration
                    </label>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Sub-broker code</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.sub_br_code}
                      onChange={(e) => update({ sub_br_code: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Sub-broker ARN</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.sub_br_arn}
                      onChange={(e) => update({ sub_br_arn: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Partner ID</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.partner_id}
                      onChange={(e) => update({ partner_id: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {step === 4 && String(form.request_type).toUpperCase() === "ENTRY" && (
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small">Member mandate ID</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.member_mandate_id}
                      onChange={(e) => update({ member_mandate_id: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">UMRN</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.umrn_number}
                      onChange={(e) => update({ umrn_number: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Utility code</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.utility_code}
                      onChange={(e) => update({ utility_code: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">Sponsor code</label>
                    <input
                      className="form-control"
                      style={inputStyle}
                      value={form.sponsor_code}
                      onChange={(e) => update({ sponsor_code: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={goBack}
                  disabled={stepIndex === 0 || submitting}
                >
                  Back
                </button>
                {!isLastStep ? (
                  <button
                    type="button"
                    className="btn text-white"
                    style={{ background: TEAL, border: "none" }}
                    onClick={goNext}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn text-white"
                    style={{ background: TEAL, border: "none" }}
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? "Submitting…" : "Register mandate"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
